import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

const EX1 =
  "https://images.unsplash.com/photo-1519222970733-f546218fa6d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzbGVlayUyMHdlYnNpdGUlMjBkZXNpZ24lMjBvbiUyMHNjcmVlbnxlbnwwfHx8fDE3ODI1MTg5ODZ8MA&ixlib=rb-4.1.0&q=85";
const EX2 =
  "https://images.unsplash.com/photo-1602576666092-bf6447a729fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzbGVlayUyMHdlYnNpdGUlMjBkZXNpZ24lMjBvbiUyMHNjcmVlbnxlbnwwfHx8fDE3ODI1MTg5ODZ8MA&ixlib=rb-4.1.0&q=85";

const examples = [
  {
    id: "corporativo",
    image: EX1,
    eyebrow: "Sitio corporativo",
    title: "Bufete legal Hernández & Asociados",
    description:
      "Sitio web profesional para un despacho jurídico con diseño elegante, secciones de servicios, equipo legal y formulario de consulta. Optimizado para transmitir confianza y captar nuevos clientes.",
    features: [
      "Diseño elegante y serio",
      "Página por cada área de práctica",
      "Formulario de consulta integrado",
      "Optimizado para móviles y SEO",
    ],
  },
  {
    id: "ecommerce",
    image: EX2,
    eyebrow: "Tienda y catálogo",
    title: "Tienda artesanal Casa del Sur",
    description:
      "Catálogo en línea con galería de productos, carrito de compras y pasarela de pago. Diseño cálido que destaca la calidad artesanal y facilita la compra desde el celular.",
    features: [
      "Galería de productos con fotos profesionales",
      "Carrito de compras y pago en línea",
      "Panel para administrar productos",
      "Integración con WhatsApp para pedidos",
    ],
  },
];

const Ejemplos = () => {
  return (
    <div data-testid="page-examples">
      <section className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block w-12 h-px bg-[#D4AF37]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">Portafolio</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
              Ejemplos de páginas que <span className="text-[#D4AF37]">podemos crear para ti</span>
            </h1>
            <p className="mt-6 text-zinc-300 text-base md:text-lg leading-relaxed">
              Dos ejemplos representativos de lo que diseñamos. Cada proyecto es
              único: tu sitio se construye desde cero con tu marca, contenido y
              objetivos.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-20 md:space-y-28">
          {examples.map((ex, i) => (
            <motion.article
              key={ex.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`example-${ex.id}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#D4AF37]/15 to-transparent blur-2xl" />
                <div className="relative border border-white/10 bg-[#0a0a0a]">
                  <img
                    src={ex.image}
                    alt={ex.title}
                    className="w-full h-[360px] md:h-[480px] object-cover"
                    loading="lazy"
                  />
                </div>
              </div>

              <div>
                <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-3">
                  {ex.eyebrow}
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight">
                  {ex.title}
                </h2>
                <p className="mt-5 text-zinc-300 text-base md:text-lg leading-relaxed">
                  {ex.description}
                </p>
                <ul className="mt-8 space-y-3">
                  {ex.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-zinc-200">
                      <Check className="w-5 h-5 text-[#D4AF37] mt-0.5" strokeWidth={2} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex flex-wrap gap-4">
                  <WhatsAppButton
                    testId={`ex-wa-${ex.id}`}
                    label="Quiero algo así"
                    variant="primary"
                    message={`Hola Rincon Web, me interesa un sitio como el ejemplo "${ex.title}". ¿Me pueden dar más información?`}
                  />
                  <a
                    data-testid={`ex-pkg-${ex.id}`}
                    href="/paquetes"
                    className="inline-flex items-center gap-2 px-8 py-4 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
                  >
                    Ver paquetes <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Ejemplos;
